import React, { useEffect, useState, useCallback } from "react";
import {
  Autosuggest,
  BreadcrumbGroup,
  Header,
  Input,
  Link as ExternalLink,
  Multiselect,
  SpaceBetween,
  StatusIndicator,
  Table,
  Box,
} from "@cloudscape-design/components";
import { Link } from "react-router-dom";
import { lambdaFunctionUrl } from "./awsConsoleUrls";
import { getAwsCreds, getDefaultRegion } from "./awsCredentials";
import { apiFetch } from "./apiFetch";
import { useDocumentTitle } from "./useDocumentTitle";

interface Account {
  documentId: string;
  Alias: string;
  accountid: string;
}

interface LambdaFunction {
  functionName: string;
  runtime: string;
  handler: string;
  codeSize: number;
  lastModified: string;
  memorySize: number;
  timeout: number;
  state: string;
  architecture: string;
}

interface AccountState {
  functions: LambdaFunction[];
  loading: boolean;
  error: string | null;
}

export default function LiveLambdaPage() {
  useDocumentTitle("Lambda");
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<{ label: string; value: string }[]>([]);
  const [region, setRegion] = useState(getDefaultRegion());
  const [filters, setFilters] = useState({ runtime: "", state: "", name: "" });
  const [accountStates, setAccountStates] = useState<Record<string, AccountState>>({});

  useEffect(() => {
    (async () => {
      const res = await apiFetch(`/accounts`);
      const { data } = await res.json();
      setAccounts(data);
      if (data.length > 0) {
        setSelectedOptions([{ label: `${data[0].Alias} (${data[0].accountid})`, value: data[0].documentId }]);
      }
    })();
  }, []);

  const selectedAccounts = accounts.filter((a) =>
    selectedOptions.some((o) => o.value === a.documentId)
  );

  const allFunctions = selectedAccounts.flatMap((a) => accountStates[a.documentId]?.functions || []);

  const fetchFunctions = useCallback(
    async (account: Account) => {
      const id = account.documentId;
      setAccountStates((prev) => ({ ...prev, [id]: { functions: [], loading: true, error: null } }));

      const creds = getAwsCreds(account.Alias);
      if (!creds) {
        setAccountStates((prev) => ({ ...prev, [id]: { functions: [], loading: false, error: "AWS credentials missing for this account." } }));
        return;
      }

      try {
        const res = await apiFetch(`/inventory/live/lambda?accountId=${id}&region=${region}`, {
          headers: {
            "X-AWS-Access-Key-Id": creds.accessKeyId,
            "X-AWS-Secret-Access-Key": creds.secretAccessKey,
            "X-AWS-Session-Token": creds.sessionToken,
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setAccountStates((prev) => ({ ...prev, [id]: { functions: data.functions, loading: false, error: null } }));
      } catch (e: any) {
        setAccountStates((prev) => ({ ...prev, [id]: { functions: [], loading: false, error: e.message } }));
      }
    },
    [region]
  );

  useEffect(() => {
    selectedAccounts.forEach((account) => fetchFunctions(account));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions, region]);

  const filteredFunctions = (functions: LambdaFunction[]) =>
    functions.filter(
      (f) =>
        (!filters.runtime || f.runtime === filters.runtime) &&
        (!filters.state || f.state === filters.state) &&
        (!filters.name || f.functionName.toLowerCase().includes(filters.name.toLowerCase()))
    );

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup
        items={[
          { text: "Live Inventory", href: "/live-inventory" },
          { text: "Lambda", href: "/live-inventory/lambda" },
        ]}
      />

      <div className="filter-bar">
        <Multiselect
          selectedOptions={selectedOptions}
          onChange={({ detail }) => setSelectedOptions(detail.selectedOptions as any)}
          options={accounts.map((a) => ({ label: `${a.Alias} (${a.accountid})`, value: a.documentId }))}
          placeholder="Select accounts"
          filteringType="auto"
        />
        <Input value={region} onChange={({ detail }) => setRegion(detail.value)} placeholder="Region" />
        <Autosuggest
          value={filters.runtime}
          onChange={({ detail }) => setFilters((f) => ({ ...f, runtime: detail.value }))}
          options={Array.from(new Set(allFunctions.map((f) => f.runtime))).filter(Boolean).map((v) => ({ value: v }))}
          placeholder="Runtime"
          enteredTextLabel={(v) => `Use: "${v}"`}
        />
        <Autosuggest
          value={filters.state}
          onChange={({ detail }) => setFilters((f) => ({ ...f, state: detail.value }))}
          options={Array.from(new Set(allFunctions.map((f) => f.state))).filter(Boolean).map((v) => ({ value: v }))}
          placeholder="State"
          enteredTextLabel={(v) => `Use: "${v}"`}
        />
        <Autosuggest
          value={filters.name}
          onChange={({ detail }) => setFilters((f) => ({ ...f, name: detail.value }))}
          options={Array.from(new Set(allFunctions.map((f) => f.functionName))).filter(Boolean).map((v) => ({ value: v }))}
          placeholder="Name"
          enteredTextLabel={(v) => `Use: "${v}"`}
        />
      </div>

      {selectedAccounts.map((account) => {
        const st = accountStates[account.documentId] || { functions: [], loading: false, error: null };

        if (st.error) {
          return (
            <Box key={account.documentId}>
              <Header>{account.Alias} ({account.accountid})</Header>
              <StatusIndicator type="error">{st.error}</StatusIndicator>
            </Box>
          );
        }

        return (
          <Table
            key={account.documentId}
            header={<Header>{account.Alias} ({account.accountid})</Header>}
            columnDefinitions={[
              {
                id: "functionName",
                header: "Function Name",
                cell: (f: LambdaFunction) => (
                  <Link to={`/live-inventory/lambda/${f.functionName}?account=${account.documentId}&region=${region}`}>
                    {f.functionName}
                  </Link>
                ),
                sortingField: "functionName",
              },
              { id: "runtime", header: "Runtime", cell: (f: LambdaFunction) => f.runtime, sortingField: "runtime" },
              { id: "memorySize", header: "Memory (MB)", cell: (f: LambdaFunction) => f.memorySize, sortingField: "memorySize" },
              { id: "timeout", header: "Timeout (s)", cell: (f: LambdaFunction) => f.timeout, sortingField: "timeout" },
              { id: "lastModified", header: "Last Modified", cell: (f: LambdaFunction) => f.lastModified, sortingField: "lastModified" },
              {
                id: "state",
                header: "State",
                cell: (f: LambdaFunction) => (
                  <StatusIndicator type={f.state === "Active" ? "success" : "info"}>
                    {f.state}
                  </StatusIndicator>
                ),
                sortingField: "state",
              },
              {
                id: "console",
                header: "AWS Console",
                cell: (f: LambdaFunction) =>
                  f.functionName && region ? (
                    <ExternalLink
                      external
                      href={lambdaFunctionUrl(f.functionName, region)}
                      externalIconAriaLabel="Opens in AWS Console"
                    />
                  ) : (
                    "—"
                  ),
              },
            ]}
            items={filteredFunctions(st.functions)}
            loading={st.loading}
            sortingDisabled={false}
            resizableColumns
            wrapLines
            stickyHeader
            empty="No Lambda functions found."
          />
        );
      })}
    </SpaceBetween>
  );
}
